import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import RightTimeApi from "@/API";
import { createFieldTranslator, type FieldTranslationRow } from "@/utils/fieldTranslation";
import { filterSupportedLanguages, type LanguageLike } from "@/utils/languageUtils";
import { readLanguageIdFromSession } from "@/i18n/sessionUser";

type I18nContextValue = {
  languageId: number;
  languages: LanguageLike[];
  fieldTranslations: FieldTranslationRow[];
  loading: boolean;
  t: ReturnType<typeof createFieldTranslator>;
  refreshFieldTranslations: () => Promise<void>;
  syncLanguageFromSession: () => void;
  setLanguageId: (languageId: number) => void;
};

const I18nContext = createContext<I18nContextValue | null>(null);

type I18nProviderProps = {
  children: ReactNode;
  languageId?: number;
};

export function I18nProvider({ children, languageId: languageIdOverride }: I18nProviderProps) {
  const [languageId, setLanguageId] = useState(() => languageIdOverride ?? readLanguageIdFromSession());
  const [languages, setLanguages] = useState<LanguageLike[]>([]);
  const [fieldTranslations, setFieldTranslations] = useState<FieldTranslationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const effectiveLanguageId = languageIdOverride ?? languageId;

  const syncLanguageFromSession = useCallback(() => {
    if (languageIdOverride != null) return;
    setLanguageId(readLanguageIdFromSession());
  }, [languageIdOverride]);

  const refreshFieldTranslations = useCallback(async () => {
    setLoading(true);
    try {
      const [translationsResp, languagesResp] = await Promise.all([
        RightTimeApi.getAsync("/FieldTranslations"),
        RightTimeApi.getAsync("/Languages"),
      ]);

      if (translationsResp?.ok && Array.isArray(translationsResp.jsonData)) {
        setFieldTranslations(translationsResp.jsonData);
      } else {
        setFieldTranslations([]);
      }

      if (languagesResp?.ok && Array.isArray(languagesResp.jsonData)) {
        setLanguages(filterSupportedLanguages(languagesResp.jsonData));
      } else {
        setLanguages([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshFieldTranslations();
  }, [refreshFieldTranslations]);

  useEffect(() => {
    if (languageIdOverride != null) {
      setLanguageId(languageIdOverride);
    }
  }, [languageIdOverride]);

  const t = useMemo(
    () => createFieldTranslator(fieldTranslations, effectiveLanguageId),
    [fieldTranslations, effectiveLanguageId]
  );

  const value = useMemo<I18nContextValue>(() => ({
    languageId: effectiveLanguageId,
    languages,
    fieldTranslations,
    loading,
    t,
    refreshFieldTranslations,
    syncLanguageFromSession,
    setLanguageId,
  }), [
    effectiveLanguageId,
    languages,
    fieldTranslations,
    loading,
    t,
    refreshFieldTranslations,
    syncLanguageFromSession,
  ]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}

export function useOptionalI18n(): I18nContextValue | null {
  return useContext(I18nContext);
}
